import apiClient from './client'

export type PhotoStatus = 'Private' | 'Public'

export const PHOTO_STATUSES: PhotoStatus[] = ['Public', 'Private']

export interface PhotoItem {
  id: number
  photo_url?: string
  filename: string
  display_name: string
  photo_content_type?: string
  title?: string
  description?: string
  alt_text?: string
  person_id: number
  status: PhotoStatus
  update_date?: string
}

export interface PaginatedPhotos {
  items: PhotoItem[]
  total: number
  page: number
  size: number
  pages: number
  has_next: boolean
  has_prev: boolean
}

export const photoApi = {
  listByPerson: async (
    personId: number,
    page = 1,
    size = 20,
    status?: PhotoStatus,
  ): Promise<PaginatedPhotos> => {
    const params: Record<string, any> = { person_id: personId, page, size }
    if (status) params.status = status
    const { data } = await apiClient.get('/photos/', { params })
    return data
  },

  get: async (id: number): Promise<PhotoItem> => {
    const { data } = await apiClient.get(`/photos/${id}`)
    return data
  },

  upload: async (
    personId: number,
    file: File,
    opts?: { title?: string; description?: string; status?: PhotoStatus }
  ): Promise<PhotoItem> => {
    const form = new FormData()
    form.append('person_id', String(personId))
    form.append('file', file)
    if (opts?.title) form.append('title', opts.title)
    if (opts?.description) form.append('description', opts.description)
    if (opts?.status) form.append('status', opts.status)
    const { data } = await apiClient.post('/photos/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  update: async (
    id: number,
    payload: { title?: string; description?: string; alt_text?: string; status?: PhotoStatus }
  ): Promise<void> => {
    const form = new FormData()
    if (payload.title !== undefined) form.append('title', payload.title)
    if (payload.description !== undefined) form.append('description', payload.description)
    if (payload.alt_text !== undefined) form.append('alt_text', payload.alt_text)
    if (payload.status !== undefined) form.append('status', payload.status)
    await apiClient.put(`/photos/${id}`, form)
  },

  setStatus: async (id: number, status: PhotoStatus): Promise<void> => {
    // Reuses the bulk-status endpoint, which performs a direct
    // UPDATE … SET status = … WHERE id = ?, so it does not depend on
    // model-attribute reflection in BaseRepository.update_by_id.
    await apiClient.post('/photos/bulk-status', {
      photo_ids: [id],
      status,
    })
  },

  bulkUpdateStatus: async (
    photoIds: number[],
    status: PhotoStatus,
  ): Promise<{ success: boolean; updated: number; status: PhotoStatus }> => {
    const { data } = await apiClient.post('/photos/bulk-status', {
      photo_ids: photoIds,
      status,
    })
    return data
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/photos/${id}`)
  },
}
